import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { UserService } from './user.service';
import { CustomHttpClient } from './custom-http-client.service';
import {GlobalErrorHandlerService} from "./global-error.service";

export interface SessionConfig {
  timeoutMinutes: number;
  warningMinutes: number;
}

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private timeoutDuration: number = 10 * 60 * 1000; // Default 10 minutes in milliseconds
  private warningTime: number = 1 * 60 * 1000; // 1 minute before expiry
  private timeoutTimer: Subscription | null = null;
  private warningTimer: Subscription | null = null;
  private lastActivityTime: number = Date.now();

  // Subject to notify components about warning
  public showWarning$ = new Subject<boolean>();

  // Subject to notify about session expiry
  public sessionExpired$ = new Subject<void>();

  // Track the last visited route
  private lastVisitedRoute: string = '';

  // Flag to check if monitoring is active
  private isMonitoring: boolean = false;

  constructor(
    private router: Router,
    private userService: UserService,
    private httpClient: CustomHttpClient,
    private globalErrorHandlerService: GlobalErrorHandlerService
  ) { }


  public initializeSessionTimeout(): void {
    // Only initialize for Practice users
    if (!this.isPracticeUser()) {
      return;
    }

    // Fetch timeout configuration from backend
    this.fetchSessionConfig().then(config => {
      // Backend returns session_expiry_minutes property
      if (config && config.session_expiry_minutes) {
        // Convert minutes to milliseconds
        const timeoutMinutesValue = this.convertBackendValueToMinutes(config.session_expiry_minutes);

        // Skip if keep_alive is set
        if (timeoutMinutesValue === -1) {
          this.stopMonitoring();
          return;
        }

        this.timeoutDuration = timeoutMinutesValue * 60 * 1000;
        console.log('Timeout duration set to ' + this.timeoutDuration + ' ms (' + timeoutMinutesValue + ' minutes)');
        this.warningTime = 1 * 60 * 1000; // Always 1 minute warning

        this.startMonitoring();
      } else {
        this.timeoutDuration = 10 * 60 * 1000;
        this.startMonitoring();
      }
    });
  }


  private convertBackendValueToMinutes(backendValue: string): number {
    const mapping: { [key: string]: number } = {
      'ten': 10,
      'thirty': 30,
      'sixty': 60,
      'ninety': 90,
      'one_twenty': 120,
      'two_fourty': 240,
      'keep_alive': -1
    };

    return mapping[backendValue] || 10; // Default to 10 minutes
  }

  private isPracticeUser(): boolean {

    const currentUser = this.userService.current_user;
    return currentUser && currentUser.userType === 'practice';
  }


  private fetchSessionConfig(): Promise<any> {
    return this.httpClient.get('practices/get_session_timeout')
      .toPromise()
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error('Error fetching session config:', error);
        // Return default fallback with correct property name
        return { session_expiry_minutes: 'ten' };
      });
  }

  public startMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.lastActivityTime = Date.now();
    this.resetTimers();
  }


  public stopMonitoring(): void {
    this.isMonitoring = false;
    this.clearTimers();
  }


  public resetTimers(): void {
    if (!this.isMonitoring || !this.isPracticeUser()) {
      return;
    }

    this.lastActivityTime = Date.now();
    this.clearTimers();

    // Calculate when to show warning (timeout - 1 minute)
    const warningDelay = this.timeoutDuration - this.warningTime;

    // Set warning timer
    this.warningTimer = timer(warningDelay).subscribe(() => {
      this.showWarningModal();
    });

    // Set timeout timer
    this.timeoutTimer = timer(this.timeoutDuration).subscribe(() => {
      this.handleSessionExpiry();
    });
  }


  private clearTimers(): void {
    if (this.warningTimer) {
      this.warningTimer.unsubscribe();
      this.warningTimer = null;
    }

    if (this.timeoutTimer) {
      this.timeoutTimer.unsubscribe();
      this.timeoutTimer = null;
    }
  }


  private showWarningModal(): void {
    this.showWarning$.next(true);
  }


  public extendSession(): void {
    this.showWarning$.next(false);
    this.resetTimers();
  }


  private handleSessionExpiry(): void {
    this.stopMonitoring();
    this.showWarning$.next(false);
    this.sessionExpired$.next();

    // Save last visited route before logout
    this.saveLastVisitedRoute();

    // Logout the user
    this.logoutUser();
  }


  public saveLastVisitedRoute(): void {
    const currentRoute = this.router.url;
    // Only track the route internally, don't save to localStorage yet
    // It will be saved to localStorage only when session expires
    this.lastVisitedRoute = currentRoute;
  }


  public getLastVisitedRoute(): string | null {
    return localStorage.getItem('sessionExpiredRoute');
  }


  public clearLastVisitedRoute(): void {
    localStorage.removeItem('sessionExpiredRoute');
  }


  private logoutUser(): void {
    // Save session expired message BEFORE logout (as logout clears localStorage items)
    localStorage.setItem('sessionExpiredMessage', 'Your session has been expired. Please log in again.');

    // Save the tracked route BEFORE logout (use lastVisitedRoute, not current router.url)
    const routeToRestore = this.lastVisitedRoute || this.router.url;
    localStorage.setItem('sessionExpiredRoute', routeToRestore);

    // Call logout (which navigates to home page)
    this.userService.logoutReason = 'session_timeout';
    this.userService.logout();

    this.checkSessionExpiredMessage();
  }


  public updateTimeoutDuration(minutes: number): void {
    if (minutes === -1) {
      // Keep alive - stop monitoring
      this.stopMonitoring();
      return;
    }

    this.timeoutDuration = minutes * 60 * 1000;

    if (this.isMonitoring) {
      this.resetTimers();
    }
  }


  public isCurrentlyMonitoring(): boolean {
    return this.isMonitoring;
  }


  private checkSessionExpiredMessage(): void {
    // Check for session expired message on page load
    setTimeout(() => {
      const message = localStorage.getItem('sessionExpiredMessage');
      if (message) {
        this.globalErrorHandlerService.error = message;
        localStorage.removeItem('sessionExpiredMessage');
      }
    }, 1500);
  }
}
